import { Component, inject, OnInit, Renderer2 } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { SyncService } from 'src/app/sync.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { NotificationType } from 'carbon-components-angular/notification/notification-content.interface';
import { DOCUMENT } from '@angular/common';

@Component({
	selector: 'login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.css'],
	animations: [
		trigger('expandHeight', [
			transition(':enter', [
				style({ height: '0px' }),
				animate('50ms ease-in', style({ height: '*' }))
			]),
			transition(':leave', [
				animate('50ms ease-out', style({ height: '0px' }))
			])
		]),
		trigger('fadeIn', [
			transition(':enter', [
				style({ opacity: 0}),
				animate('125ms ease-in', style({ opacity: 1 }))
			]),
			transition(':leave', [
				animate('125ms ease-out', style({ opacity: 0}))
			])
		]),
		trigger('fadeInUp', [
			transition(':enter', [
				style({ opacity: 0, transform: 'translateY(-2px)' }),
				animate('125ms ease-in', style({ opacity: 1, transform: 'translateY(0)' }))
			]),
			transition(':leave', [
				animate('125ms ease-out', style({ opacity: 0, transform: 'translateY(2px)' }))
			])
		])
	]
})
export class LoginComponent implements OnInit {

	authStatus = 'unauthenticated';

	email = '';
	emailInvalid = false;
	emailInvalidText = '';

	password = '';
	passwordInvalid = false;
	passwordInvalidText = '';

	subscription: any;
	showNotification: any;
	notificationType: NotificationType = 'success';
	notificationTitle = '';
	workspace = '';

	document = inject(DOCUMENT);
	renderer = inject(Renderer2);
	theme = 'white';

	constructor(
		private syncService: SyncService,
		private afAuth: AngularFireAuth,
		private afStore: AngularFirestore,
		private http: HttpClient
	) {}

	navigateToPage(path: string) {
		this.syncService.updatePage(path);
	}

	ngOnInit(): void {
		this.syncService.authStatus$.subscribe((data) => {
			this.authStatus = data;
		});

		const date = new Date();
		const hours = date.getHours();
		if (hours >= 6 && hours < 18) {
			this.theme = 'white';
		} else {
			this.theme = 'g100';
		}
		this.renderer.addClass(this.document.body, this.theme);
		this.syncService.updateTheme(this.theme);
	}

	onKeyUpEmail($event: KeyboardEvent) {
		this.email = ($event.target as HTMLInputElement).value;
		this.syncService.updateEmail(this.email);
	}

	validateEmail() {
		this.emailInvalid = false;
		const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
		if (this.email.trim() === '') {
			this.emailInvalid = true;
			this.emailInvalidText = 'Email cannot be empty';
		} else if (!emailRegex.test(this.email)) {
			this.emailInvalid = true;
			this.emailInvalidText = 'Invalid email format';
		}
	}

	onKeyUpPassword($event: KeyboardEvent) {
		this.password = ($event.target as HTMLInputElement).value;
		this.syncService.updatePassword(this.password);
	}

	validatePassword() {
		this.passwordInvalid = false;
		if (this.password.trim() === '') {
			this.passwordInvalid = true;
			this.passwordInvalidText = 'Password cannot be empty';
		}
	}

	async resetPassword() {
		this.validateEmail();
		if (this.emailInvalid) {
			return;
		}
		this.afAuth.sendPasswordResetEmail(this.email)
		.then(() => {
			this.showNotification = false;
			setTimeout(() => {
				this.notificationType = 'success' as NotificationType;
				this.notificationTitle = 'Password reset email sent';
				this.showNotification = true;
			}, 0);
		})
		.catch((error) => {
			this.showNotification = false;
			setTimeout(() => {
				this.notificationType = 'danger' as NotificationType;
				this.notificationTitle = 'Email not found';
				this.showNotification = true;
			}, 0);
		});
	}

	async login() {
		this.validateEmail();
		this.validatePassword();
		if (this.emailInvalid || this.passwordInvalid) {
			return;
		}
		this.updateAuthStatus('authenticating');
		this.afAuth.signInWithEmailAndPassword(this.email, this.password).then(async () => {
			if (this.email.endsWith('multiplai.co')) {
				this.workspace = 'MultiplAI';
			}
			if (this.email.endsWith('industryventures.com')) {
				this.workspace = 'Industry Ventures';
			}
			if (this.email.endsWith('multiplai-test.co')) {
				this.workspace = 'MultiplAI Testing';
			}
			if (this.email.endsWith('multiplai-demo.co')) {
				this.workspace = 'Your Organization';
			}
			if (this.email.endsWith('seinecap.com')) {
				this.workspace = 'Seine Capital';
			}
			this.syncService.updateWorkspace(this.workspace);

			const fileSnapshot = await this.afStore.collection(
			  'Users', ref => ref
				.where('Workspace', '==', this.workspace)
				).snapshotChanges();
				this.subscription = fileSnapshot.subscribe(docs => {
				if (docs.length === 0) {
					this.syncService.updateUserData(null);
					this.updateAuthStatus('authenticated');
					if (this.subscription) {
						this.subscription.unsubscribe();
					}
					return;
				} else {
					const userDoc = docs[0].payload.doc;
					const userData = userDoc.data();
					// @ts-ignore
					userData['Email'] = this.email; // DONE
					this.syncService.updateUserId(userDoc.id);
					this.syncService.updateUserData(userData);
					// @ts-ignore
					this.syncService.updateWorkspace(userData['Workspace']);
					// @ts-ignore
					if (userData['Current Deal']) {
						// @ts-ignore
						this.syncService.updateCurrentDeal(userData['Current Deal']);
					}
					// @ts-ignore
					if (userData['Current File']) {
						// @ts-ignore
						this.syncService.updateFileId(userData['Current File']);
					}
					// @ts-ignore
					this.updateAuthStatus('authenticated');
					if (this.subscription) {
						this.subscription.unsubscribe();
					}
					// @ts-ignore
					this.syncService.updatePage(userData['Page']);
				}
			  });
		}).catch((response: any) => {
			this.emailInvalid = true;
			this.emailInvalidText = '';
			this.passwordInvalid = true;
			this.passwordInvalidText = 'Email or password incorrect';
			this.updateAuthStatus('unauthenticated');
			if (this.subscription) {
				this.subscription.unsubscribe();
			}
		});
		const formData: FormData = new FormData();
		formData.append('Field', '000-Asset Name');
		formData.append('Value', 'Crescent Asia');
		formData.append('Page URL', 'https://firebasestorage.googleapis.com/v0/b/multiplai-test.appspot.com/o/demo%40multiplai.co__HW%20Nov%2019__Kallang%20%3E%202022-12-31_2022%20Q4%20Quarterly%20Portfolio%20Report.pdf__Page%200?alt=media');
		// @ts-ignore
		formData.append('Polygons Document', 'Zf9ueWTLW0At49IQrHnK');
		const headers = new HttpHeaders();
		headers.append('Content-Type', 'multipart/form-data');
		this.http.post(environment.polygonUrl, formData, { headers, responseType: 'json' });
	}

	updateAuthStatus(status: string) {
		this.authStatus = status;
		this.syncService.updateAuthStatus(status);
	}
}
